import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

export interface RepInsight {
  insight: string;
  recommendedScriptType: string;
  weakestCompetency: string;
}

interface RepInsightsListProps {
  insights: RepInsight[];
  isLoading: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  selectedInsightIndex: number | null;
  onSelectInsight: (index: number, scriptType: string) => void;
}

const RepInsightsList: React.FC<RepInsightsListProps> = ({
  insights,
  isLoading,
  isFetching,
  onRefresh,
  selectedInsightIndex,
  onSelectInsight,
}) => {
  const { isDark } = useDashboardTheme();
  if (isLoading || (isFetching && insights.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4} mb={4} sx={{ background: "var(--color-bg)", borderRadius: "16px", border: "1px solid var(--color-border)" }}>
        <CircularProgress size={24} sx={{ color: "var(--color-stat-accent)", mr: 2 }} />
        <Typography variant="body1" sx={{ color: "var(--color-text)", opacity: 0.8 }}>Analyzing rep's performance...</Typography>
      </Box>
    );
  }

  if (!insights || insights.length === 0) {
    return (
      <Box mb={4} p={3} sx={{
        background: isDark ? "rgba(108,99,255,0.06)" : "rgba(108,99,255,0.03)",
        border: isDark ? "1px dashed rgba(108,99,255,0.25)" : "1px dashed rgba(108,99,255,0.2)",
        borderRadius: "16px",
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="overline" sx={{ color: isDark ? "#A78BFA" : "#5B21B6", fontWeight: "bold", letterSpacing: 1.5 }}>
            AI Coaching Insights
          </Typography>
          <Button
            onClick={onRefresh}
            disabled={isFetching}
            startIcon={<RefreshIcon />}
            sx={{
              color: isDark ? "#A78BFA" : "#5B21B6",
              textTransform: "none",
              fontSize: "0.8rem",
              opacity: 0.8,
              minWidth: "auto",
              "&:hover": { opacity: 1, backgroundColor: "rgba(108,99,255,0.1)" }
            }}
          >
            {isFetching ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ color: "var(--color-text)", opacity: 0.7, fontSize: "0.9rem" }}>
          No previous sessions (pitches, practices, meetings, or recordings) found for this rep. Complete or upload sessions to generate personalized AI coaching insights.
        </Typography>
      </Box>
    );
  }

  return (
    <Box mb={4} p={3} sx={{
      background: isDark ? "rgba(108,99,255,0.08)" : "rgba(108,99,255,0.04)",
      border: isDark ? "1px solid rgba(108,99,255,0.2)" : "1px solid rgba(108,99,255,0.15)",
      borderRadius: "16px",
      position: "relative"
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="overline" sx={{ color: isDark ? "#A78BFA" : "#5B21B6", fontWeight: "bold", letterSpacing: 1.5 }}>
          AI Coaching Insights
        </Typography>
        <Button
          onClick={onRefresh}
          disabled={isFetching}
          startIcon={<RefreshIcon />}
          sx={{
            color: isDark ? "#A78BFA" : "#5B21B6",
            textTransform: "none",
            fontSize: "0.8rem",
            opacity: 0.8,
            minWidth: "auto",
            "&:hover": { opacity: 1, backgroundColor: "rgba(108,99,255,0.1)" }
          }}
        >
          {isFetching ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ color: "var(--color-text)", opacity: 0.8, mb: 3 }}>
        Select a focus area below to configure the script type.
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        {insights.map((insight, index) => (
          <Box
            key={index}
            onClick={() => onSelectInsight(index, insight.recommendedScriptType)}
            sx={{
              p: 2,
              borderRadius: "12px",
              cursor: "pointer",
              border: selectedInsightIndex === index 
                ? `2px solid ${isDark ? "#A78BFA" : "#5B21B6"}` 
                : "1px solid var(--color-border)",
              backgroundColor: selectedInsightIndex === index 
                ? "rgba(108,99,255,0.15)" 
                : "var(--color-surface)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: selectedInsightIndex === index ? (isDark ? "#A78BFA" : "#5B21B6") : "var(--color-text)"
              }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem", color: "var(--color-text)" }}>
              Needs focus on: <span style={{ color: "var(--color-stat-accent)" }}>{insight.weakestCompetency}</span>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "var(--color-text)", opacity: 0.8, lineHeight: 1.6 }}>
              {insight.insight}
            </Typography>
            <Box mt={2} display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" sx={{ color: "var(--color-muted)" }}>
                Recommended script type:
              </Typography>
              <Box sx={{ 
                px: 2, 
                py: 0.5, 
                backgroundColor: selectedInsightIndex === index ? "rgba(108,99,255,0.4)" : "rgba(108,99,255,0.2)", 
                color: selectedInsightIndex === index ? "#fff" : (isDark ? "#A78BFA" : "#5B21B6"), 
                borderRadius: "8px", 
                fontWeight: "bold", 
                textTransform: "capitalize", 
                fontSize: "0.85rem" 
              }}>
                {insight.recommendedScriptType}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RepInsightsList;
